import React, { useEffect, useState } from "react";
import { ApiClient } from "../../utils/api-client";
import Header from "../home/header";
import Loading from "../home/Loading";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { TenderStatusList } from "../../utils/types";

const TenderEditForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();

    const loginState = sessionStorage.getItem( "loginState" ) ? JSON.parse( sessionStorage.getItem( "loginState" ) ) : null;
    const apiClient = new ApiClient( loginState?.accessToken );

    const [ isLoading, setIsLoading ] = useState( false );
    const [ isFetching, setIsFetching ] = useState( false );
    const [ feedback, setFeedback ] = useState( "" );


    const formatNum = ( val ) => {
        if ( val === null || val === undefined || val === "" ) return "";
        const parts = val.toString().split( "." );
        parts[ 0 ] = parts[ 0 ].replace( /\B(?=(\d{3})+(?!\d))/g, "," );
        return parts.join( "." );
    };

    const handleNumericChange = ( field ) => ( e ) => {
        const raw = e.target.value.replace( /,/g, "" );
        if ( !/^\d*(\.\d*)?$/.test( raw ) ) return;
        formik.setFieldValue( field, raw );
    };

    const formik = useFormik( {
        initialValues: {
            title: "",
            organisation: "",
            status: "preparation",
            tenderedAmount: "",
            bankLoan: {
                bankName: "",
                loanAmount: "",
                interest: "",
                tenure: "",
            },
            expenses: [ { description: "", amount: "" } ],
        },
        enableReinitialize: true,
        validationSchema: Yup.object( {
            title: Yup.string().required( "Tender title is required" ),
            organisation: Yup.string().required( "Organisation is required" ),
            status: Yup.string().required( "Status is required" ),
            tenderedAmount: Yup.string().required( "Tendered amount is required" ),
            expenses: Yup.array().of(
                Yup.object( {
                    description: Yup.string().required( "Description is required" ),
                    amount: Yup.string().required( "Amount is required" ),
                } )
            ),
        } ),
        onSubmit: async ( values ) => {
            if ( !id ) return;
            setIsLoading( true );
            setFeedback( "" );
            try {
                const payload = {
                    title: values.title,
                    organisation: values.organisation,
                    status: values.status,
                    tenderedAmount: values.tenderedAmount,
                    bankLoan: values.bankLoan,
                    expenses: values.expenses,
                };
                const res = await apiClient.patch( `tenders/${id}`, payload );
                if ( res.status === 200 ) {
                    navigate( `/tenders/view/${id}`, { replace: true } );
                } else {
                    setFeedback( "Failed to update tender." );
                }
            } catch ( e ) {
                setFeedback( "Failed to update tender." );
            } finally {
                setIsLoading( false );
            }
        },
    } );

    const addExpense = () => {
        formik.setFieldValue( "expenses", [
            ...formik.values.expenses,
            { description: "", amount: "" },
        ] );
    };

    const removeExpense = ( idx ) => {
        formik.setFieldValue(
            "expenses",
            formik.values.expenses.filter( ( _, i ) => i !== idx )
        );
    };

    const loadData = async () => {
        if ( !id ) return;
        setIsFetching( true );
        setFeedback( "" );
        try {
            const req = await apiClient.get( `tenders/${id}` );
            if ( req.status === 200 && req.data ) {
                const tender = req.data;
                formik.setValues( {
                    title: tender.title || "",
                    organisation: tender.organisation || "",
                    status: tender.status || "preparation",
                    tenderedAmount: tender.tenderedAmount ? String( tender.tenderedAmount ) : "",
                    bankLoan: {
                        bankName: tender?.bankLoan?.bankName || "",
                        loanAmount: tender?.bankLoan?.loanAmount ? String( tender.bankLoan.loanAmount ) : "",
                        interest: tender?.bankLoan?.interest ? String( tender.bankLoan.interest ) : "",
                        tenure: tender?.bankLoan?.tenure ? String( tender.bankLoan.tenure ) : "",
                    },
                    expenses: Array.isArray( tender.expenses ) && tender.expenses.length > 0
                        ? tender.expenses.map( ex => ( {
                            description: ex.description || "",
                            amount: ex.amount ? String( ex.amount ) : "",
                        } ) )
                        : [ { description: "", amount: "" } ],
                } );
            } else {
                setFeedback( "Tender not found." );
            }
        } catch ( e ) {
            const fallback = location.state;
            if ( fallback?.id === id ) {
                formik.setValues( {
                    title: fallback.title || "",
                    organisation: fallback.organisation || "",
                    status: fallback.status || "preparation",
                    tenderedAmount: fallback.tenderedAmount ? String( fallback.tenderedAmount ) : "",
                    bankLoan: {
                        bankName: fallback?.bankLoan?.bankName || "",
                        loanAmount: fallback?.bankLoan?.loanAmount ? String( fallback.bankLoan.loanAmount ) : "",
                        interest: fallback?.bankLoan?.interest ? String( fallback.bankLoan.interest ) : "",
                        tenure: fallback?.bankLoan?.tenure ? String( fallback.bankLoan.tenure ) : "",
                    },
                    expenses: Array.isArray( fallback.expenses ) && fallback.expenses.length > 0
                        ? fallback.expenses.map( ex => ( {
                            description: ex.description || "",
                            amount: ex.amount ? String( ex.amount ) : "",
                        } ) )
                        : [ { description: "", amount: "" } ],
                } );
            } else {
                setFeedback( "Failed to load tender details." );
            }
        } finally {
            setIsFetching( false );
        }
    };

    useEffect( () => {
        if ( !loginState ) {
            navigate( "/signin", { replace: true } );
            return;
        }
        loadData();
    }, [ id ] );

    return (
        <>
            <Header auth={loginState} />
            {( isLoading || isFetching ) && <Loading />}
            <section className="flex justify-start items-start">
                <div className="container mx-auto md:mt-8">
                    {feedback && (
                        <div className="w-10/12 md:w-8/12 mx-auto mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-sm" id="form-feedback">{feedback}</div>
                    )}
                    <div className="md:w-10/12 md:mx-auto rounded-xl border border-gray-200 bg-white shadow-md pt-10 px-3 md:px-6">
                        <div className="text-center mx-auto w-full">
                            <p className="text-sm uppercase tracking-wide text-gray-500 text-center">Tender Setup</p>
                            <p className="text-center text-2xl font-semibold text-gray-800 my-3">Edit Tender</p>
                            <form
                                id="tender_edit_form"
                                className="text-start w-10/12 md:w-9/12 mx-auto mb-16"
                                onSubmit={formik.handleSubmit}
                            >
                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="title">Tender Title <em className="text-danger">*</em></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="title"
                                        name="title"
                                        value={formik.values.title}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.title && formik.errors.title && (
                                        <div className="text-danger small">{formik.errors.title}</div>
                                    )}
                                </div>

                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="organisation">Organisation <em className="text-danger">*</em></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="organisation"
                                        name="organisation"
                                        value={formik.values.organisation}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    />
                                    {formik.touched.organisation && formik.errors.organisation && (
                                        <div className="text-danger small">{formik.errors.organisation}</div>
                                    )}
                                </div>

                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="status">Status <em className="text-danger">*</em></label>
                                    <select
                                        className="form-control"
                                        id="status"
                                        name="status"
                                        value={formik.values.status}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                    >
                                        {TenderStatusList.map( s => (
                                            <option key={s.value} value={s.value}>{s.label}</option>
                                        ) )}
                                    </select>
                                    {formik.touched.status && formik.errors.status && (
                                        <div className="text-danger small">{formik.errors.status}</div>
                                    )}
                                </div>

                                <div className="form-group my-2 w-10/12 md:w-8/12 lg:w-8/12 mx-auto">
                                    <label htmlFor="tenderedAmount">Tendered Amount TZS <em className="text-danger">*</em></label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        id="tenderedAmount"
                                        name="tenderedAmount"
                                        value={formatNum( formik.values.tenderedAmount )}
                                        onChange={handleNumericChange( "tenderedAmount" )}
                                        onBlur={formik.handleBlur}
                                        inputMode="decimal"
                                        autoComplete="off"
                                    />
                                    {formik.touched.tenderedAmount && formik.errors.tenderedAmount && (
                                        <div className="text-danger small">{formik.errors.tenderedAmount}</div>
                                    )}
                                </div>

                                <div className="w-10/12 md:w-8/12 lg:w-8/12 mx-auto my-4">
                                    <p className="fw-semibold text-base mb-2 border-b pb-1">Bank Loan (optional)</p>
                                    <div className="form-group my-2">
                                        <label htmlFor="bankLoan.bankName">Bank Name</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="bankLoan.bankName"
                                            name="bankLoan.bankName"
                                            value={formik.values.bankLoan.bankName}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                    </div>
                                    <div className="form-group my-2">
                                        <label htmlFor="bankLoan.loanAmount">Loan Amount TZS</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="bankLoan.loanAmount"
                                            name="bankLoan.loanAmount"
                                            value={formatNum( formik.values.bankLoan.loanAmount )}
                                            onChange={handleNumericChange( "bankLoan.loanAmount" )}
                                            onBlur={formik.handleBlur}
                                            inputMode="decimal"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <div className="flex gap-4">
                                        <div className="form-group my-2 flex-1">
                                            <label htmlFor="bankLoan.interest">Interest (%)</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="bankLoan.interest"
                                                name="bankLoan.interest"
                                                value={formik.values.bankLoan.interest}
                                                onChange={handleNumericChange( "bankLoan.interest" )}
                                                onBlur={formik.handleBlur}
                                                inputMode="decimal"
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="form-group my-2 flex-1">
                                            <label htmlFor="bankLoan.tenure">Tenure (months)</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="bankLoan.tenure"
                                                name="bankLoan.tenure"
                                                value={formik.values.bankLoan.tenure}
                                                onChange={handleNumericChange( "bankLoan.tenure" )}
                                                onBlur={formik.handleBlur}
                                                inputMode="numeric"
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="w-10/12 md:w-8/12 lg:w-8/12 mx-auto my-4">
                                    <div className="flex justify-between items-center border-b pb-1 mb-2">
                                        <p className="fw-semibold text-base">Expenses Incurred</p>
                                        <button
                                            type="button"
                                            className="btn btn-sm bg-primary text-white"
                                            onClick={addExpense}
                                        >+ Add</button>
                                    </div>
                                    {formik.values.expenses.map( ( expense, idx ) => (
                                        <div key={idx} className="flex gap-3 items-start my-2">
                                            <div className="form-group flex-1">
                                                {idx === 0 && <label>Description</label>}
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`expenses[${idx}].description`}
                                                    value={expense.description}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                />
                                            </div>
                                            <div className="form-group w-36">
                                                {idx === 0 && <label>Amount TZS</label>}
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name={`expenses[${idx}].amount`}
                                                    value={formatNum( expense.amount )}
                                                    onChange={( e ) => {
                                                        const raw = e.target.value.replace( /,/g, "" );
                                                        if ( !/^\d*(\.\d*)?$/.test( raw ) ) return;
                                                        formik.setFieldValue( `expenses[${idx}].amount`, raw );
                                                    }}
                                                    onBlur={formik.handleBlur}
                                                />
                                            </div>
                                            {formik.values.expenses.length > 1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm text-danger mt-auto mb-1"
                                                    onClick={() => removeExpense( idx )}
                                                    title="Remove"
                                                >✕</button>
                                            )}
                                        </div>
                                    ) )}
                                </div>

                                <div className="form-group my-4 w-10/12 md:w-8/12 lg:w-8/12 mx-auto flex gap-3">
                                    <button
                                        type="submit"
                                        className="btn flex-1 bg-success text-white"
                                        disabled={isLoading}
                                    >
                                        {isLoading ? "Updating..." : "Update Tender"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn flex-1 bg-secondary text-white"
                                        onClick={() => navigate( `/tenders/view/${id}`, { replace: true } )}
                                        disabled={isLoading}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};
export default TenderEditForm;
